import { Box, toast } from '@palmetto/palmetto-components';
import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import { OrganizationStatus, OrganizationStatusLabels, OrganizationType, ProgramType, UserPermissions } from 'types';

import { flattenToOptions } from '../../../helpers/flattenOrgs';
import useIsIntegratedPartnerUser from '../../../hooks/useIsIntegratedPartnerUser';
import usePermissions from '../../../hooks/usePermissions';
import { isErrorWithData, isErrorWithErrors } from '../../../services/helpers';
import {
  useCreateOrganizationMutation,
  useGetOrganizationTreeQuery,
  useLazyGetOrganizationDetailsQuery,
} from '../../../services/organizations';
import { useAuth } from '../../auth/authProvider';
import { isPalmettoFinanceUser } from '../../auth/RequirePalmettoFinanceUser';
import { MainContainer } from '../../MainContainer';
import PageHeader from '../../PageHeader';

import { OrganizationForm } from './OrganizationForm';

const organizationStatusOptions = Object.keys(OrganizationStatus).map((key) => ({
  label: OrganizationStatusLabels[key as OrganizationStatus],
  value: key,
}));

export function Create() {
  const { data: { tree = [] } = {}, isLoading } = useGetOrganizationTreeQuery();
  const { claims } = useAuth();
  const [createOrganization] = useCreateOrganizationMutation();
  const [getOrganizationDetails, { data: organizationDetails, isLoading: isParentOrgLoading }] =
    useLazyGetOrganizationDetailsQuery();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const startingParentAlias = searchParams.get('parent');
  const permissions = usePermissions();
  const isIntegratedPartnerUser = useIsIntegratedPartnerUser();

  const parentOptions = useMemo(() => {
    return flattenToOptions(tree);
  }, [tree]);

  const defaultParentAlias = useMemo(
    () => parentOptions.find((p) => p.value === (startingParentAlias ? startingParentAlias : claims?.org)),
    [parentOptions, startingParentAlias, claims],
  );

  useEffect(() => {
    if (defaultParentAlias) {
      getOrganizationDetails({ alias: defaultParentAlias.value })
        .unwrap()
        .catch((error) => {
          toast.error('Error fetching parent organization details');
          console.error('Error fetching parent organization details:', error);
        });
    }
  }, [defaultParentAlias, getOrganizationDetails]);

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      setSubmitting(true);

      if (
        !values.programTypes.includes(ProgramType.hvac) &&
        !values.programTypes.includes(ProgramType.waterHeater)
      ) {
        values.extendedWarrantyDealerInformation = undefined;
      }
      await createOrganization({
        name: values.name,
        alias: values.alias,
        parentAlias: values.parent.value,
        address: values.address1,
        city: values.city,
        state: values.state,
        zip: values.zip,
        licenses: values.stateLicense,
        insuranceCompanyPhoneNumber: values.insuranceCompanyPhoneNumber,
        insuranceCompanyName: values.insuranceCompanyName,
        email: values.email,
        phoneNumber: values.phoneNumber,
        federalEmployerId: values.federalEmployerId,
        programTypes: values.programTypes,
        organizationTypes: values.organizationTypes,
        extendedWarrantyDealerInformation: values.extendedWarrantyDealerInformation,
        status: values.status.value,
        accountBundlingEnabled: values.accountBundlingEnabled,
      }).unwrap();
      toast.success('Organization created successfully');
      navigate('/settings/organizations');
    } catch (e) {
      if (isErrorWithErrors(e)) {
        const errorMessage = e.data.message;
        toast.error(`${errorMessage}: ${e.data.errors.map((e) => e).join(', ')}`);
      } else if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(`Error creating organization: ${errorMessage}`);
      } else {
        console.error(e);
        toast.error('Error creating organization');
      }
    }
    setSubmitting(false);
  };

  const initialValues = useMemo(() => {
    return {
      name: '',
      alias: '',
      parent: defaultParentAlias,
      status: organizationStatusOptions.find((s) => s.value === OrganizationStatus.active),
      address1: '',
      city: '',
      state: '',
      zip: '',
      phoneNumber: '',
      email: '',
      ...(isPalmettoFinanceUser(claims) &&
        permissions.includes(UserPermissions.admin) && {
          stateLicense: [{ state: '', contractorLicenseNumber: '', additionalLicenseNumbers: '' }],
        }),
      programTypes: [organizationDetails?.programTypes?.[0] || ProgramType.solar],
      organizationTypes: [OrganizationType.epc],
      extendedWarrantyDealerInformation: [],
      accountBundlingEnabled: false,
    };
  }, [defaultParentAlias, claims, permissions, organizationDetails?.programTypes]);

  if (isIntegratedPartnerUser) {
    return <Navigate to="/settings/organizations" replace />;
  }

  return (
    <>
      <Helmet>
        <title>Create Organizations | Settings</title>
      </Helmet>
      <MainContainer>
        <PageHeader
          title="Create An Organization"
          eyebrow={<Link to="/settings/organizations">Organizations</Link>}
        />
        <Box
          padding={{
            base: '0 lg',
            tablet: '0',
          }}
        >
          <OrganizationForm
            initialValues={initialValues}
            handleSubmit={handleSubmit}
            isLoading={isLoading || isParentOrgLoading}
            parentOptions={parentOptions}
            statusOptions={organizationStatusOptions}
            isCreateMode={true}
          />
        </Box>
      </MainContainer>
    </>
  );
}
